import { Text, useColorModeValue } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { forwardRef, useCallback, useMemo, useState } from "react";

import CompanyDisplay from "@/common/components/CompanyDisplay";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import {
  ListType,
  MultiSelector,
} from "@/common/components/MultiSelector/MultiSelector";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { companyQueryKeys } from "@/company/data/companyQueryKeys";
import { useCompaniesSearch } from "@/company/data/useCompaniesSearch";
import { useCompanyNamesData } from "@/company/data/useCompanyNamesData";
import { GroupSummary } from "@/feature/components/AccessSummary";
import { COMPANY_ID_FIELD_KEY } from "@reflag/shared/attributeFilter";
import { CompanyName } from "@reflag/shared/companyAPI";
import { CompanyIdsFilter } from "@reflag/shared/filter";

export const CompanyIdsFilterItem = ({
  filter,
}: {
  filter: CompanyIdsFilter;
}) => {
  const { data: companyNames = [] } = useCompanyNamesData(
    filter.ids.slice(0, 3),
    false,
  );

  const avatarBackgroundColor = useColorModeValue("gray.50", "gray.750");

  return (
    <GroupSummary
      avatarBackgroundColor={avatarBackgroundColor}
      count={filter.ids.length}
      entityType="company"
      sampleEntities={companyNames.slice(0, 3)}
    />
  );
};

export const CompanyIdsFilterFields = () => {
  return (
    <ManagedFormControl
      name="ids"
      render={({ field }) => <CompaniesSelector {...field} />}
    />
  );
};

type CompaniesSelectorProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

export const CompaniesSelector = forwardRef<
  HTMLInputElement,
  CompaniesSelectorProps
>(({ value, onChange: onChangeProp }, ref) => {
  const { appId, envId } = useEnvironment();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [listType, setListType] = useState<ListType>("all");

  const { data: companyNames = [] } = useCompanyNamesData(value);

  // todo: remove unknown companies when useCompaniesSearch supports it
  const unknownCompanies = useMemo(() => {
    return companyNames.filter(
      (c) => c.isUnknown && (!searchValue || c.id.includes(searchValue)),
    );
  }, [companyNames, searchValue]);

  const {
    data: companies = [],
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCompaniesSearch(searchValue, {
    filter:
      listType === "selected"
        ? {
            type: "companyAttribute",
            operator: "ANY_OF",
            field: COMPANY_ID_FIELD_KEY,
            values: value,
          }
        : undefined,
  });

  const suggestions = useMemo(() => {
    return [...unknownCompanies, ...companies];
  }, [companies, unknownCompanies]);

  const onChange = useCallback(
    (companyIds: string[], meta?: CompanyName[]) => {
      if (!onChangeProp) return;

      // Get the current list of items by ids
      const current = queryClient.getQueryData<CompanyName[]>(
        companyQueryKeys.companyNames(appId, envId, value),
      );

      // Update the list of items by ids
      queryClient.setQueryData<CompanyName[]>(
        companyQueryKeys.companyNames(appId, envId, companyIds),
        () => {
          return companyIds.map(
            (id) =>
              meta?.find((c) => c.id === id) ??
              current?.find((c) => c.id === id) ?? {
                id,
                name: null,
                avatarUrl: null,
              },
          );
        },
      );

      if (companyIds.length === 0) setListType("all");
      onChangeProp(companyIds);
    },
    [appId, envId, onChangeProp, queryClient, value],
  );

  return (
    <MultiSelector
      ref={ref}
      addRawLabel={(searchValue) => (
        <>
          Add company ID{" "}
          <Text as="span" fontWeight="bold">
            {searchValue}
          </Text>
        </>
      )}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      isLoadingSuggestions={isFetching}
      itemRenderFn={(item, search) => (
        <CompanyDisplay company={item} search={search} showId />
      )}
      listType={listType}
      mx={-4}
      noItemsFound="No companies found"
      searchPlaceholder="Search companies or enter company ID"
      searchValue={searchValue}
      suggestions={suggestions}
      value={value}
      onChange={onChange}
      onListTypeChange={setListType}
      onLoadMore={fetchNextPage}
      onSearchInput={setSearchValue}
    />
  );
});

CompaniesSelector.displayName = "CompaniesSelector";
